/**
 * Collect Payment Dialog — offline payments with mixed methods.
 * Supports Cash, UPI, Cards, NEFT/RTGS/IMPS, Cheque, Wallet.
 * Any un-allocated remainder auto-flows into the patient's wallet.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Wallet, Receipt, Banknote, CreditCard, Smartphone,
  Building2, FileText, Check, AlertCircle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const METHODS = [
  { key: "cash",          label: "Cash",          icon: Banknote },
  { key: "upi",           label: "UPI",           icon: Smartphone },
  { key: "credit_card",   label: "Credit card",   icon: CreditCard },
  { key: "debit_card",    label: "Debit card",    icon: CreditCard },
  { key: "bank_transfer", label: "Bank transfer", icon: Building2 },
  { key: "neft",          label: "NEFT",          icon: Building2 },
  { key: "rtgs",          label: "RTGS",          icon: Building2 },
  { key: "imps",          label: "IMPS",          icon: Building2 },
  { key: "cheque",        label: "Cheque",        icon: FileText },
  { key: "wallet",        label: "Wallet",        icon: Wallet },
];

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

export default function CollectPaymentDialog({
  open, onOpenChange, patientId, patientName, onSaved,
}) {
  const [splits, setSplits] = useState([{ method: "cash", amount: "", ref: "" }]);
  const [note, setNote] = useState("");
  const [wallet, setWallet] = useState(0);
  const [invoices, setInvoices] = useState([]);   // pending/partial
  const [allocMode, setAllocMode] = useState("auto");   // auto | manual
  const [manualAlloc, setManualAlloc] = useState({});   // {invoiceId: amount}
  const [busy, setBusy] = useState(false);

  // Load context (wallet + pending invoices) when opened.
  useEffect(() => {
    if (!open || !patientId) return;
    (async () => {
      try {
        const [w, inv] = await Promise.all([
          api.get(`/patients/${patientId}/wallet`),
          api.get(`/invoices?patient_id=${patientId}`),
        ]);
        setWallet(w.data.balance || 0);
        setInvoices(
          (inv.data || []).filter((i) =>
            ["pending", "partial"].includes(i.status)),
        );
      } catch { /* silent */ }
    })();
    setSplits([{ method: "cash", amount: "", ref: "" }]);
    setNote("");
    setAllocMode("auto");
    setManualAlloc({});
  }, [open, patientId]);

  const totalReceived = useMemo(
    () => splits.reduce((s, x) => s + Number(x.amount || 0), 0),
    [splits],
  );
  const walletSplit = useMemo(
    () => splits.filter((s) => s.method === "wallet")
                .reduce((sum, s) => sum + Number(s.amount || 0), 0),
    [splits],
  );
  const totalDue = useMemo(
    () => invoices.reduce((s, i) => s + Number(i.total || 0), 0),
    [invoices],
  );
  const totalManualAlloc = useMemo(
    () => Object.values(manualAlloc).reduce((s, v) => s + Number(v || 0), 0),
    [manualAlloc],
  );
  const remainderToWallet = Math.max(
    totalReceived - (allocMode === "manual" ? totalManualAlloc : Math.min(totalReceived, totalDue)),
    0,
  );

  const addSplit = () =>
    setSplits((p) => [...p, { method: "upi", amount: "", ref: "" }]);
  const removeSplit = (i) =>
    setSplits((p) => p.filter((_, idx) => idx !== i));
  const updateSplit = (i, patch) =>
    setSplits((p) => p.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const submit = async () => {
    if (totalReceived <= 0) {
      toast.error("Enter an amount");
      return;
    }
    if (walletSplit > wallet + 0.01) {
      toast.error(`Wallet split ₹${walletSplit} exceeds balance ₹${wallet}`);
      return;
    }
    if (allocMode === "manual" && totalManualAlloc > totalReceived + 0.01) {
      toast.error("Allocations exceed the amount received");
      return;
    }
    const body = {
      splits: splits
        .filter((s) => Number(s.amount || 0) > 0)
        .map((s) => ({
          method: s.method,
          amount: Number(s.amount),
          ref: s.ref || null,
        })),
      note: note || null,
    };
    if (allocMode === "manual") {
      body.allocations = Object.entries(manualAlloc)
        .filter(([, v]) => Number(v || 0) > 0)
        .map(([invoice_id, amount]) => ({ invoice_id, amount: Number(amount) }));
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/patients/${patientId}/payments`, body);
      const msgs = [`${INR(data.payment.total)} received`];
      if (data.payment.remainder_to_wallet > 0) {
        msgs.push(`${INR(data.payment.remainder_to_wallet)} → wallet`);
      }
      toast.success(msgs.join(" · "));
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, "Payment failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg p-0 overflow-hidden"
                     data-testid="collect-payment-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E8EAE9]">
          <div className="overline text-ink-soft">Collect payment</div>
          <DialogTitle className="font-display text-2xl text-ink">
            {patientName || "Patient"}
          </DialogTitle>
          <div className="flex items-center gap-2 text-xs mt-1">
            <Badge variant="outline" className="border-pine/20 text-ink-mid">
              <Wallet className="w-3 h-3 mr-1" /> Wallet {INR(wallet)}
            </Badge>
            {totalDue > 0 && (
              <Badge variant="outline" className="border-amber-300 text-amber-800">
                Outstanding {INR(totalDue)}
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Splits */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-ink-soft">
                Payment splits
              </div>
              <button onClick={addSplit} type="button"
                      data-testid="payment-add-split"
                      className="text-[10px] px-2 py-1 rounded-full border border-pine/20 hover:bg-linen inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {splits.map((s, i) => {
              const M = METHODS.find((m) => m.key === s.method);
              const Icon = M?.icon || Receipt;
              return (
                <div key={i} className="flex items-center gap-2"
                     data-testid={`payment-split-row-${i}`}>
                  <div className="relative flex-1">
                    <Icon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-soft" />
                    <select
                      value={s.method}
                      onChange={(e) => updateSplit(i, { method: e.target.value })}
                      data-testid={`payment-split-method-${i}`}
                      className="w-full h-10 pl-8 pr-2 rounded-xl border border-[#E8EAE9] text-sm bg-white"
                    >
                      {METHODS.map((m) => (
                        <option key={m.key} value={m.key}
                                disabled={m.key === "wallet" && wallet <= 0}>
                          {m.label}{m.key === "wallet" ? ` (${INR(wallet)})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Amount"
                    value={s.amount}
                    onChange={(e) => updateSplit(i, { amount: e.target.value })}
                    data-testid={`payment-split-amount-${i}`}
                    className="w-28"
                  />
                  <Input
                    placeholder="Ref"
                    value={s.ref}
                    onChange={(e) => updateSplit(i, { ref: e.target.value })}
                    className="w-24"
                  />
                  {splits.length > 1 && (
                    <button onClick={() => removeSplit(i)} type="button"
                            className="text-ink-soft hover:text-red-600 p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Allocation */}
          {invoices.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-ink-soft">
                  Allocation
                </div>
                <div className="flex items-center gap-1 text-[10px]" data-testid="payment-alloc-mode">
                  <button type="button"
                          onClick={() => setAllocMode("auto")}
                          data-testid="payment-alloc-auto"
                          className={`px-2 py-1 rounded-full ${allocMode==='auto'?'bg-pine text-linen':'text-ink-mid hover:bg-linen'}`}>
                    Auto (FIFO)
                  </button>
                  <button type="button"
                          onClick={() => setAllocMode("manual")}
                          data-testid="payment-alloc-manual"
                          className={`px-2 py-1 rounded-full ${allocMode==='manual'?'bg-pine text-linen':'text-ink-mid hover:bg-linen'}`}>
                    Manual
                  </button>
                </div>
              </div>

              {allocMode === "auto" && (
                <div className="text-xs text-ink-mid bg-linen/60 rounded-xl px-3 py-2">
                  We'll auto-apply this payment to the oldest outstanding invoices first. Any remainder becomes a wallet advance.
                </div>
              )}

              {allocMode === "manual" && (
                <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                  {invoices.map((inv) => (
                    <div key={inv.id} className="flex items-center gap-2 bg-white border border-[#EDEBE3] rounded-xl px-3 py-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink truncate">{inv.invoice_number}</div>
                        <div className="text-[10px] text-ink-soft">
                          {new Date(inv.created_at).toLocaleDateString("en-IN")} · {INR(inv.total)}
                          {inv.status === "partial" && " · partial"}
                        </div>
                      </div>
                      <Input
                        type="number" min={0} step="0.01"
                        placeholder="0"
                        value={manualAlloc[inv.id] || ""}
                        onChange={(e) => setManualAlloc((p) => ({ ...p, [inv.id]: e.target.value }))}
                        data-testid={`payment-alloc-${inv.id}`}
                        className="w-24 h-8"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-widest text-ink-soft">Note</label>
            <Input placeholder="Optional note" value={note}
                   onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        {/* Summary + footer */}
        <div className="border-t border-[#E8EAE9] bg-linen/50 px-5 py-3 space-y-2">
          <div className="grid grid-cols-3 gap-2 text-xs">
            <SummaryCell label="Received" value={INR(totalReceived)} tone="pine" />
            <SummaryCell
              label="Applied"
              value={INR(allocMode === "manual" ? totalManualAlloc : Math.min(totalReceived, totalDue))}
              tone="blue"
            />
            <SummaryCell
              label="→ Wallet"
              value={INR(remainderToWallet)}
              tone={remainderToWallet > 0 ? "green" : "muted"}
              icon={Wallet}
            />
          </div>
          {walletSplit > wallet + 0.01 && (
            <div className="text-[11px] text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Wallet split exceeds balance.
            </div>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={busy || totalReceived <= 0}
            data-testid="payment-submit"
            className="w-full h-10 rounded-full bg-pine text-linen text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Record payment
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCell({ label, value, tone = "pine", icon: Icon }) {
  const tones = {
    pine:  "bg-white border-[#E8EAE9] text-ink",
    blue:  "bg-blue-50 border-blue-200 text-blue-800",
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    muted: "bg-linen border-[#E8EAE9] text-ink-soft",
  };
  return (
    <div className={`rounded-xl border px-2 py-1.5 ${tones[tone]}`}>
      <div className="text-[9px] uppercase tracking-widest opacity-70 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="font-mono text-sm mt-0.5">{value}</div>
    </div>
  );
}
